import { cva, VariantProps } from 'class-variance-authority';
import styles from './margin.module.css';
import type { InputType } from 'storybook/internal/types';

const marginOptions = [
  'none',
  'auto',
  '5xs',
  '4xs',
  '3xs',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
] as const;

export const marginVariants = cva('', {
  variants: {
    margin: {
      none: styles['margin--none'],
      auto: styles['margin--auto'],
      '5xs': styles['margin--5xs'],
      '4xs': styles['margin--4xs'],
      '3xs': styles['margin--3xs'],
      '2xs': styles['margin--2xs'],
      xs: styles['margin--xs'],
      sm: styles['margin--sm'],
      md: styles['margin--md'],
      lg: styles['margin--lg'],
      xl: styles['margin--xl'],
      '2xl': styles['margin--2xl'],
      '3xl': styles['margin--3xl'],
      '4xl': styles['margin--4xl'],
      '5xl': styles['margin--5xl'],
      '6xl': styles['margin--6xl'],
    },
    marginX: {
      none: styles['margin-x--none'],
      auto: styles['margin-x--auto'],
      '5xs': styles['margin-x--5xs'],
      '4xs': styles['margin-x--4xs'],
      '3xs': styles['margin-x--3xs'],
      '2xs': styles['margin-x--2xs'],
      xs: styles['margin-x--xs'],
      sm: styles['margin-x--sm'],
      md: styles['margin-x--md'],
      lg: styles['margin-x--lg'],
      xl: styles['margin-x--xl'],
      '2xl': styles['margin-x--2xl'],
      '3xl': styles['margin-x--3xl'],
      '4xl': styles['margin-x--4xl'],
      '5xl': styles['margin-x--5xl'],
      '6xl': styles['margin-x--6xl'],
    },
    marginY: {
      none: styles['margin-y--none'],
      auto: styles['margin-y--auto'],
      '5xs': styles['margin-y--5xs'],
      '4xs': styles['margin-y--4xs'],
      '3xs': styles['margin-y--3xs'],
      '2xs': styles['margin-y--2xs'],
      xs: styles['margin-y--xs'],
      sm: styles['margin-y--sm'],
      md: styles['margin-y--md'],
      lg: styles['margin-y--lg'],
      xl: styles['margin-y--xl'],
      '2xl': styles['margin-y--2xl'],
      '3xl': styles['margin-y--3xl'],
      '4xl': styles['margin-y--4xl'],
      '5xl': styles['margin-y--5xl'],
      '6xl': styles['margin-y--6xl'],
    },
    marginTop: {
      none: styles['margin-top--none'],
      auto: styles['margin-top--auto'],
      '5xs': styles['margin-top--5xs'],
      '4xs': styles['margin-top--4xs'],
      '3xs': styles['margin-top--3xs'],
      '2xs': styles['margin-top--2xs'],
      xs: styles['margin-top--xs'],
      sm: styles['margin-top--sm'],
      md: styles['margin-top--md'],
      lg: styles['margin-top--lg'],
      xl: styles['margin-top--xl'],
      '2xl': styles['margin-top--2xl'],
      '3xl': styles['margin-top--3xl'],
      '4xl': styles['margin-top--4xl'],
      '5xl': styles['margin-top--5xl'],
      '6xl': styles['margin-top--6xl'],
    },
    marginRight: {
      none: styles['margin-right--none'],
      auto: styles['margin-right--auto'],
      '5xs': styles['margin-right--5xs'],
      '4xs': styles['margin-right--4xs'],
      '3xs': styles['margin-right--3xs'],
      '2xs': styles['margin-right--2xs'],
      xs: styles['margin-right--xs'],
      sm: styles['margin-right--sm'],
      md: styles['margin-right--md'],
      lg: styles['margin-right--lg'],
      xl: styles['margin-right--xl'],
      '2xl': styles['margin-right--2xl'],
      '3xl': styles['margin-right--3xl'],
      '4xl': styles['margin-right--4xl'],
      '5xl': styles['margin-right--5xl'],
      '6xl': styles['margin-right--6xl'],
    },
    marginBottom: {
      none: styles['margin-bottom--none'],
      auto: styles['margin-bottom--auto'],
      '5xs': styles['margin-bottom--5xs'],
      '4xs': styles['margin-bottom--4xs'],
      '3xs': styles['margin-bottom--3xs'],
      '2xs': styles['margin-bottom--2xs'],
      xs: styles['margin-bottom--xs'],
      sm: styles['margin-bottom--sm'],
      md: styles['margin-bottom--md'],
      lg: styles['margin-bottom--lg'],
      xl: styles['margin-bottom--xl'],
      '2xl': styles['margin-bottom--2xl'],
      '3xl': styles['margin-bottom--3xl'],
      '4xl': styles['margin-bottom--4xl'],
      '5xl': styles['margin-bottom--5xl'],
      '6xl': styles['margin-bottom--6xl'],
    },
    marginLeft: {
      none: styles['margin-left--none'],
      auto: styles['margin-left--auto'],
      '5xs': styles['margin-left--5xs'],
      '4xs': styles['margin-left--4xs'],
      '3xs': styles['margin-left--3xs'],
      '2xs': styles['margin-left--2xs'],
      xs: styles['margin-left--xs'],
      sm: styles['margin-left--sm'],
      md: styles['margin-left--md'],
      lg: styles['margin-left--lg'],
      xl: styles['margin-left--xl'],
      '2xl': styles['margin-left--2xl'],
      '3xl': styles['margin-left--3xl'],
      '4xl': styles['margin-left--4xl'],
      '5xl': styles['margin-left--5xl'],
      '6xl': styles['margin-left--6xl'],
    },
  },
});

export type MarginVariants = VariantProps<typeof marginVariants>;

export const marginArgTypes: Record<string, InputType> = {
  margin: {
    control: 'select',
    options: marginOptions,
    description:
      '**[Responsive]** margin on all sides using design system spacing tokens',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginX: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** Horizontal margin (left and right)',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginY: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** Vertical margin (top and bottom)',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginTop: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** margin on the top side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginRight: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** margin on the right side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginBottom: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** margin on the bottom side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  marginLeft: {
    control: 'select',
    options: marginOptions,
    description: '**[Responsive]** margin on the left side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
};
